import { type ComputedRef, type MaybeRef, computed, unref } from 'vue';
import { type UseQueryReturnType, useQuery } from '@tanstack/vue-query';
import type { Contact } from '~/interfaces/contacts/main';
import { type ContactDetailResult, type ContactsListResult, getContactDetail, getContactsList } from '~/api/contacts';

export const contactsKeys = {
  root: () => ['contacts'] as const,
  list: (params: string) => ['contacts', 'list', params] as const,
  detail: (id: string | number) => ['contacts', 'detail', String(id)] as const,
};

export function useContactsListQuery (
  params: MaybeRef<string | null>,
): {
    query: UseQueryReturnType<ContactsListResult, Error>;
    contacts: ComputedRef<Contact[]>;
    totalRecords: ComputedRef<number>;
  } {
  const queryKey = computed(() =>
    contactsKeys.list(String(unref(params) ?? '')),
  );

  const query = useQuery<ContactsListResult>({
    queryKey,
    queryFn: () => getContactsList(unref(params)),
  });

  const contacts = computed<Contact[]>(() => query?.data?.value?.items ?? []);
  const totalRecords = computed<number>(() => query?.data?.value?.totalItems ?? 0);

  return {
    query,
    contacts,
    totalRecords,
  };
}

export function useContactDetailQuery (
  contactId: MaybeRef<string | number | null | undefined>,
): {
    query: UseQueryReturnType<ContactDetailResult, Error>;
    contact: ComputedRef<Contact | null>;
  } {
  const enabled = computed(() => Boolean(unref(contactId)));

  const queryKey = computed(() =>
    contactsKeys.detail(String(unref(contactId) ?? '')),
  );

  const query = useQuery<ContactDetailResult>({
    queryKey,
    queryFn: () => {
      const id = unref(contactId);
      if (!id) { throw new Error('Contact ID is required'); }
      return getContactDetail(String(id));
    },
    enabled,
  });

  const contact = computed<Contact | null>(() => query?.data?.value?.item ?? null);

  return {
    query,
    contact,
  };
}
